import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Users, UserCheck, UserX, Upload, TrendingUp, Calendar, FileText, Download, FileSpreadsheet, AlertCircle, CheckCircle, Phone, PhoneIncoming } from "lucide-react";
import axiosInstance from "../config/axiosConfig";
import QuickActionBar from "../components/shared/QuickActionBar"; // Task 33: Unified Quick-Action Layer
import CallLogsTab from "./CallLogsTab";
import { getRecentActiveJDs } from "../utils/dashboardData";

// Toast notification component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-teal/10 border-teal/50' : 'bg-red-500/10 border-red-500/50';
  const textColor = type === 'success' ? 'text-teal' : 'text-red-400';
  const Icon = type === 'success' ? CheckCircle : AlertCircle;

  return (
    <div className={`fixed top-4 right-4 z-50 ${bgColor} border rounded-lg p-4 flex items-center space-x-3 animate-slide-in shadow-xl`}>
      <Icon className={textColor} size={20} />
      <p className={`${textColor} font-body`}>{message}</p>
      <button onClick={onClose} className={`${textColor} hover:opacity-70 transition-opacity ml-2`}>
        ×
      </button>
    </div>
  );
}

function Dashboard() {
  const [currentUser, setCurrentUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [jds, setJds] = useState([]);  // Task 20: Add JD state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");  // Task 29: Add tab state
  const [exportDates, setExportDates] = useState({
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch current user
      const userResponse = await axiosInstance.get('/auth/me');
      setCurrentUser(userResponse.data);

      // Fetch candidates
      const candidatesResponse = await axiosInstance.get('/candidates');
      setCandidates(candidatesResponse.data || []);

      // Fetch call logs - with error handling
      try {
        const logsResponse = await axiosInstance.get('/call_logs');
        setCallLogs(logsResponse.data || []);
      } catch (callLogError) {
        console.error('Call logs error:', callLogError);
        setCallLogs([]);  // Set empty array on error
      }

      // Task 20: Fetch recent JDs
      const jdsResponse = await axiosInstance.get('/jds/list?status_filter=Active');
      setJds(getRecentActiveJDs(jdsResponse.data));
    } catch (err) {
      setError("Failed to load dashboard data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async () => {
    setExportLoading(true);

    try {
      const params = {};
      
      if (exportDates.from_date) {
        params.from_date = exportDates.from_date;
      }
      if (exportDates.to_date) {
        params.to_date = exportDates.to_date;
      }

      // Include subordinates for managers
      if (currentUser && ['admin', 'bm', 'am', 'tl'].includes(currentUser.role)) {
        params.include_subordinates = true;
      }

      const response = await axiosInstance.get('/reports/call-logs/export', {
        params,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const role = currentUser?.role || 'user';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `SPES_Call_Report_${role}_${timestamp}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setToast({ message: '✅ Report exported successfully!', type: 'success' });
    } catch (err) {
      console.error('Export error:', err);
      setToast({ message: '❌ Failed to export report. Please try again.', type: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  // Compute stats
  const totalCandidates = candidates.length;
  const activeCandidates = candidates.filter(c => c.status === "Active").length;
  const inactiveCandidates = candidates.filter(c => c.status !== "Active").length;
  const totalCallLogs = callLogs.length;
  
  // Calls in last 7 days
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentCalls = callLogs.filter(log => {
    if (!log.call_time) return false;
    return new Date(log.call_time) >= sevenDaysAgo;
  }).length;
  
  // Recent candidates (last 5 by created_at)
  const recentCandidates = [...candidates]
    .filter(c => c.created_at)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  // Role-based labels
  const getRoleLabel = (role) => {
    const labels = {
      'admin': { text: 'Admin', color: 'bg-gold/20 text-gold border-gold/30' },
      'bm': { text: 'Branch Manager', color: 'bg-teal/20 text-teal border-teal/30' },
      'am': { text: 'Account Manager', color: 'bg-teal-light/20 text-teal-light border-teal-light/30' },
      'tl': { text: 'Team Lead', color: 'bg-blue-400/20 text-blue-400 border-blue-400/30' },
      'recruiter': { text: 'Recruiter', color: 'bg-textLight/20 text-textLight border-textLight/30' }
    };
    return labels[role] || { text: role, color: 'bg-textLight/20 text-textLight border-textLight/30' };
  };

  const roleInfo = currentUser ? getRoleLabel(currentUser.role) : null;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal border-t-transparent mb-4"></div>
        <div className="text-textLight/70 font-body">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center space-x-3">
        <AlertCircle className="text-red-400" size={20} />
        <span className="text-red-400 font-body">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notifications */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Header with User Info */}
      <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal to-gold">
              Welcome, {currentUser?.name || 'User'}
            </h1>
            {roleInfo && (
              <div className="mt-2 flex items-center space-x-2">
                <span className={`px-3 py-1 text-sm rounded-full font-medium border ${roleInfo.color}`}>
                  {roleInfo.text}
                </span>
                <span className="text-textLight/60 text-sm font-body">
                  {currentUser?.email}
                </span>
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-textLight/60 font-body">Your Dashboard</p>
              <p className="text-xs text-textLight/40 font-body mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Task 29: Tab Navigation */}
      <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg">
        <div className="flex space-x-1 p-1">
          <button
            onClick={() => setActiveTab("overview")}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === "overview"
                ? "bg-teal/20 text-teal border border-teal/30"
                : "text-textLight/70 hover:text-textLight hover:bg-navy-dark"
            }`}
          >
            <TrendingUp size={18} />
            <span>Overview</span>
          </button>
          <button
            onClick={() => setActiveTab("calls")}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === "calls"
                ? "bg-teal/20 text-teal border border-teal/30"
                : "text-textLight/70 hover:text-textLight hover:bg-navy-dark"
            }`}
          >
            <PhoneIncoming size={18} />
            <span>Call Logs</span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "calls" ? (
        <CallLogsTab currentUser={currentUser} />
      ) : (
        <>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Total Candidates */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6 hover:shadow-teal/20 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-textLight/70">Total Candidates</p>
              <p className="text-3xl font-heading font-bold text-teal mt-2">{totalCandidates}</p>
            </div>
            <div className="bg-teal/20 p-3 rounded-lg">
              <Users size={24} className="text-teal" />
            </div>
          </div>
        </div>

        {/* Active Candidates */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6 hover:shadow-teal/20 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-textLight/70">Active</p>
              <p className="text-3xl font-heading font-bold text-teal-light mt-2">{activeCandidates}</p>
            </div>
            <div className="bg-teal/20 p-3 rounded-lg">
              <UserCheck size={24} className="text-teal-light" />
            </div>
          </div>
        </div>

        {/* Inactive Candidates */}
        <div className="bg-navy-light border border-textLight/20 rounded-lg shadow-lg p-6 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-textLight/70">Inactive</p>
              <p className="text-3xl font-heading font-bold text-textLight/60 mt-2">{inactiveCandidates}</p>
            </div>
            <div className="bg-textLight/10 p-3 rounded-lg">
              <UserX size={24} className="text-textLight/60" />
            </div>
          </div>
        </div>

        {/* Total Call Logs */}
        <div className="bg-navy-light border border-gold/30 rounded-lg shadow-lg p-6 hover:shadow-gold/20 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-textLight/70">Total Calls</p>
              <p className="text-3xl font-heading font-bold text-gold mt-2">{totalCallLogs}</p>
            </div>
            <div className="bg-gold/20 p-3 rounded-lg">
              <FileText size={24} className="text-gold" />
            </div>
          </div>
        </div>

        {/* Calls Last 7 Days */}
        <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg p-6 hover:shadow-teal/20 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-textLight/70">Calls (7 days)</p>
              <p className="text-3xl font-heading font-bold text-teal-light mt-2">{recentCalls}</p>
            </div>
            <div className="bg-teal/20 p-3 rounded-lg">
              <Calendar size={24} className="text-teal-light" />
            </div>
          </div>
        </div>
      </div>

      {/* Export Call Reports */}
      <div className="bg-navy-light border border-gold/30 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gold/20">
          <div className="flex items-center">
            <FileSpreadsheet className="text-gold mr-2" size={24} />
            <h2 className="text-xl font-heading font-semibold text-textLight">Export Call Reports</h2>
          </div>
          <p className="text-sm text-textLight/60 mt-1 font-body">
            {currentUser?.role === 'recruiter' 
              ? 'Export your call logs to Excel'
              : currentUser?.role === 'admin'
              ? 'Export all call logs from the organization'
              : 'Export your team\'s call logs to Excel'}
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* From Date */}
            <div>
              <label className="block text-sm font-medium text-textLight/90 mb-2 font-body">
                From Date
              </label>
              <input
                type="date"
                value={exportDates.from_date}
                onChange={(e) => setExportDates({ ...exportDates, from_date: e.target.value })}
                className="w-full px-3 py-2 bg-navy/50 border border-teal/30 rounded-lg text-textLight focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all font-body"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-sm font-medium text-textLight/90 mb-2 font-body">
                To Date
              </label>
              <input
                type="date"
                value={exportDates.to_date}
                onChange={(e) => setExportDates({ ...exportDates, to_date: e.target.value })}
                className="w-full px-3 py-2 bg-navy/50 border border-teal/30 rounded-lg text-textLight focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent transition-all font-body"
              />
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExportReport}
            disabled={exportLoading}
            className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-teal to-teal-light hover:from-teal-light hover:to-teal text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-teal/40 disabled:opacity-50 disabled:cursor-not-allowed font-heading group"
          >
            {exportLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download size={20} className="group-hover:translate-y-1 transition-transform" />
                <span>Export to Excel</span>
              </>
            )}
          </button>

          {/* Helper Text */}
          <p className="text-xs text-textLight/50 mt-3 text-center font-body">
            {currentUser?.role === 'admin' && 'Will export all call logs from the system'}
            {['bm', 'am', 'tl'].includes(currentUser?.role) && 'Will include your subordinates\' call logs'}
            {currentUser?.role === 'recruiter' && 'Will export only your call logs'}
          </p>
        </div>
      </div>

      {/* Recent Candidates */}
      <div className="bg-navy-light border border-teal/30 rounded-lg shadow-lg">
        <div className="p-6 border-b border-teal/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <TrendingUp className="text-teal mr-2" size={24} />
              <h2 className="text-xl font-heading font-semibold text-textLight">Recent Candidates</h2>
            </div>
            <Link
              to="/candidates"
              className="text-sm text-gold hover:text-gold-light font-medium transition-colors"
            >
              View All →
            </Link>
          </div>
        </div>
        
        <div className="p-6">
          {recentCandidates.length === 0 ? (
            <p className="text-textLight/60 text-center py-8 font-body">No candidates yet</p>
          ) : (
            <div className="space-y-4">
              {recentCandidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-between p-4 bg-navy rounded-lg hover:bg-navy-dark border border-teal/20 hover:border-teal/40 transition-all"
                >
                  <div className="flex-1">
                    <h3 className="font-semibold text-textLight font-body">{candidate.name}</h3>
                    <p className="text-sm text-textLight/70 font-body">{candidate.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <QuickActionBar candidate={candidate} />
                    {candidate.role && (
                      <span className="text-sm text-textLight/70 font-body">{candidate.role}</span>
                    )}
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                      candidate.status === 'Active'
                        ? 'bg-teal/20 text-teal border border-teal/30'
                        : 'bg-textLight/10 text-textLight/60 border border-textLight/20'
                    }`}>
                      {candidate.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Task 20: Recent Job Descriptions */}
      <div className="bg-navy-light border border-gold/30 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gold/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="text-gold mr-2" size={24} />
              <h2 className="text-xl font-heading font-semibold text-textLight">Recent Job Descriptions</h2>
            </div>
            <Link
              to="/jd-upload"
              className="text-sm text-gold hover:text-gold-light font-medium transition-colors"
            >
              View All →
            </Link>
          </div>
        </div>

        <div className="p-6">
          {jds.length === 0 ? (
            <p className="text-textLight/60 text-center py-8 font-body">No job descriptions yet</p>
          ) : (
            <div className="space-y-4">
              {jds.map((jd) => (
                <Link
                  key={jd.id}
                  to={`/jd/${jd.id}`}
                  className="block p-4 bg-navy rounded-lg hover:bg-navy-dark border border-gold/20 hover:border-gold/40 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-textLight font-body group-hover:text-gold transition-colors">
                        {jd.role_title}
                      </h3>
                      <p className="text-sm text-textLight/70 font-body mt-1">{jd.client_name}</p>
                      {jd.location && (
                        <p className="text-xs text-textLight/60 mt-1 flex items-center">
                          <span>{jd.location}</span>
                          {jd.experience_range && <span className="mx-2">•</span>}
                          {jd.experience_range && <span>{jd.experience_range}</span>}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        jd.status === 'Active'
                          ? 'bg-gold/20 text-gold border border-gold/30'
                          : 'bg-textLight/10 text-textLight/60 border border-textLight/20'
                      }`}>
                        {jd.status}
                      </span>
                      {jd.assigned_to && jd.assigned_to.length > 0 && (
                        <p className="text-xs text-textLight/60 mt-1">
                          {jd.assigned_to.length} assigned
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-navy-light border border-gold/30 rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-heading font-semibold mb-4 text-gold">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/upload"
            className="bg-gold/10 border border-gold/30 hover:bg-gold/20 hover:border-gold rounded-lg p-4 transition-all group"
          >
            <Upload size={24} className="mb-2 text-gold group-hover:text-gold-light transition-colors" />
            <h3 className="font-heading font-semibold text-textLight">Bulk Upload</h3>
            <p className="text-sm text-textLight/70 mt-1">Upload candidates via CSV/XLSX</p>
          </Link>
          <Link
            to="/candidates"
            className="bg-teal/10 border border-teal/30 hover:bg-teal/20 hover:border-teal rounded-lg p-4 transition-all group"
          >
            <Users size={24} className="mb-2 text-teal group-hover:text-teal-light transition-colors" />
            <h3 className="font-heading font-semibold text-textLight">Manage Candidates</h3>
            <p className="text-sm text-textLight/70 mt-1">View and manage all candidates</p>
          </Link>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
